import React, {useCallback, useMemo, useRef} from 'react';
import type {SxProps} from '@mui/material';
import {Grid, Typography, useTheme} from '@mui/material';
import assert from 'assert-ts';
import {produce} from 'immer';
import get from 'lodash/get';
import set from 'lodash/set';
import {useNavigate} from 'react-router';
import type {SetApprovedValue} from 'schema/form/types';
import type {DataFormConfiguration} from 'schema/types';
import type {ChangeRequestAction, Part, Schema} from 'schemaDefinition/types';
import type {AgentWorkSummary, NationalAgent} from 'services/agent/types';
import {isFeatureEnabled} from 'configuration';
import {useLocalization} from 'localization';
import {getActualDataChangesWithSchema} from 'services/metadata/functions/getActualDataChangesWithSchema';
import {Card, FlexBox, Layout, Spacer, Text} from 'components';
import {DataFormV1, Preview} from 'schema';
import {DataFormCompareLayout} from 'schema/form/components/DataFormCompareLayout';
import {AgentWorksTable} from 'widgets/agentWorks';
import {useAgentNoraf} from '../hooks/useAgentNoraf';

type Props = {
  id: string;
  schema: Schema;
  data: NationalAgent;
  formConfiguration: DataFormConfiguration<NationalAgent>;
  showValidationErrors: boolean;
  onDataChanged: (data: NationalAgent) => void;
};

export const EditNationalAgent = ({
  id,
  schema,
  data,
  formConfiguration,
  showValidationErrors,
  onDataChanged,
}: Props) => {
  const {t, tLoose} = useLocalization();
  const navigate = useNavigate();
  const {colors} = useTheme();

  const primaryColorSx: SxProps = useMemo(
    () => ({
      color: colors.burgundy,
    }),
    [colors],
  );

  const dataRef = useRef(data);

  const {data: norafData} = useAgentNoraf(data);

  const compareSchema = useMemo(() => {
    return {
      ...schema,
      parts: [
        ...schema.parts.filter(
          p =>
            (p as Part).name !== 'nameVariants' &&
            (p as Part).name !== 'internalComments',
        ),
      ],
    };
  }, [schema]);

  const nameVariantsPreviewSchema = useMemo(() => {
    return {
      ...schema,
      parts: [...schema.parts.filter(p => (p as Part).name === 'nameVariants')],
    };
  }, [schema]);

  const {actualChanges, trimmedSchema} = useMemo(() => {
    const changes = norafData
      ? getActualDataChangesWithSchema(
          norafData,
          data,
          compareSchema,
          undefined,
          {
            excludeEmpty: true,
            excludeEqual: true,
            excludeReadOnly: true,
          },
        )
      : undefined;

    return {
      ...changes,
    };
  }, [norafData, compareSchema, data]);

  const setApprovedValue: SetApprovedValue = useCallback(
    (rootValuePath: string, itemAction: ChangeRequestAction) => {
      if (itemAction === 'replace') {
        const newData = produce(data, draft => {
          set(draft, rootValuePath, get(norafData, rootValuePath));
        });
        onDataChanged(newData);
      } else {
        assert.soft(false, 'EditNationalAgent: Expected itemAction replace');
      }
    },
    [norafData, data, onDataChanged],
  );

  const TypeDifference = useMemo(() => {
    if (
      norafData?.agentType &&
      norafData.agentType !== dataRef.current.agentType
    ) {
      return (
        <Card sx={{width: '100%', mb: 4}}>
          <Typography variant="h4">
            {t('page.agents.noraf.type.diff', {
              type: tLoose(`general.entity.${norafData?.agentType}`),
            })}
          </Typography>
        </Card>
      );
    }

    return null;
  }, [norafData, t, tLoose]);

  const onRowClick = useCallback(
    ({id}: Pick<AgentWorkSummary, 'id'>) => {
      return navigate(`/metadata/${id}`);
    },
    [navigate],
  );

  return (
    <Grid container spacing={4} sx={{width: '100%'}}>
      <Grid size={{xs: 7}}>
        <DataFormV1
          id={id}
          schema={schema}
          data={data}
          configuration={formConfiguration}
          mode="edit"
          relatedData={{}}
          showErrors={showValidationErrors}
          onDataChanged={onDataChanged}
          originalData={norafData}
        />
        <Spacer height={2} />
        <AgentWorksTable agent={data} onRowClick={onRowClick} />
      </Grid>
      <Grid size={{xs: 5}}>
        <Layout>
          {isFeatureEnabled('compareNationalAgent') &&
          actualChanges &&
          trimmedSchema ? (
            <>
              {TypeDifference}
              <FlexBox horizontal pb={1} px={2} flex={1}>
                <DataFormCompareLayout
                  renderCurrent={() => (
                    <Text
                      variant="h4"
                      sx={primaryColorSx}
                      textTransform="uppercase">
                      {t(`page.metadata.cr.BARE_CHANGES.currentValues.title`)}
                    </Text>
                  )}
                  renderProposed={() => (
                    <Text
                      variant="h4"
                      sx={primaryColorSx}
                      textTransform="uppercase">
                      {t(`page.metadata.cr.BARE_CHANGES.changedValues.title`)}
                    </Text>
                  )}
                />
              </FlexBox>
              <DataFormV1
                id={id}
                schema={trimmedSchema}
                originalData={dataRef.current}
                data={actualChanges}
                configuration={formConfiguration}
                mode="compare"
                relatedData={{}}
                showErrors={showValidationErrors}
                onDataChanged={onDataChanged}
                setApprovedValue={setApprovedValue}
              />
              <Spacer height={4} />
            </>
          ) : null}

          {isFeatureEnabled('compareNationalAgent') && norafData ? (
            <Layout>
              <FlexBox horizontal pb={1} px={2} flex={1}>
                <Text
                  variant="h4"
                  sx={primaryColorSx}
                  textTransform="uppercase">
                  {t('page.agents.nameVariants.noraf')}
                </Text>
              </FlexBox>
              <Preview schema={nameVariantsPreviewSchema} data={norafData} />
            </Layout>
          ) : null}
        </Layout>
      </Grid>
    </Grid>
  );
};
